import React from 'react'
import styled, { createGlobalStyle, css, keyframes } from 'styled-components'

const RADAR_POINTS = [
  { key: 'point_top_right', top: '14.04px', left: '61.21px', size: '4.786px', opacity: 0.8 },
  { key: 'point_mid_left', top: '36.9px', left: '14.71px', size: '6.417px', opacity: 0.4 },
  { key: 'point_mid_right', top: '52.94px', left: '82.89px', size: '6.417px', opacity: 1 },
  { key: 'point_bottom_left', top: '76.9px', left: '19.41px', size: '6.382px', opacity: 0.2 },
  { key: 'point_top_left', top: '8px', left: '24.67px', size: '6.382px', opacity: 0.6 },
]

const RadarContainer = styled.div`
  width: 100px;
  height: 96.365px;
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RadarCircle = styled.div`
  width: 96.365px;
  height: 96.365px;
  border-radius: 50%;
  position: relative;
  overflow: visible;
`

const RadarSweepProperty = createGlobalStyle`
  @property --radar-sweep-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: true;
  }
`

const radarSweepAngle = keyframes`
  from { --radar-sweep-angle: 0deg; }
  to { --radar-sweep-angle: 360deg; }
`

const dashedSweepMask = css`
  --radar-sweep-angle: 0deg;
  -webkit-mask-image: conic-gradient(
    from var(--radar-sweep-angle),
    rgba(0, 0, 0, 1) 0deg,
    rgba(0, 0, 0, 0.55) 140deg,
    rgba(0, 0, 0, 0.15) 260deg,
    rgba(0, 0, 0, 0) 330deg,
    rgba(0, 0, 0, 0) 360deg
  );
  mask-image: conic-gradient(
    from var(--radar-sweep-angle),
    rgba(0, 0, 0, 1) 0deg,
    rgba(0, 0, 0, 0.55) 140deg,
    rgba(0, 0, 0, 0.15) 260deg,
    rgba(0, 0, 0, 0) 330deg,
    rgba(0, 0, 0, 0) 360deg
  );

  @media (prefers-reduced-motion: no-preference) {
    animation: ${radarSweepAngle} 4s linear infinite;
  }
`

const ConcentricCircle = styled.div`
  position: absolute;
  border: ${(props) =>
    props.$dashed ? '0.638px dashed var(--t-colors-blue-400)' : '0.638px solid #3d58ca'};
  border-radius: 50%;

  ${(props) => props.$dashed && dashedSweepMask}
`

const radarSweep = keyframes`
  from {
    transform: translateX(-50%) rotate(0deg);
  }
  to {
    transform: translateX(-50%) rotate(360deg);
  }
`

const SweepLine = styled.div`
  position: absolute;
  left: 50%;
  bottom: 50%;
  width: 1.5px;
  height: 60px;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0.25) 80%,
    rgba(255, 255, 255, 0) 100%
  );
  transform-origin: 50% 100%;
  transform: translateX(-50%) rotate(0deg);
  z-index: 5;
  pointer-events: none;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${radarSweep} 4s linear infinite;
  }
`

const CenterDot = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 7.82px;
  height: 7.82px;
  border-radius: 50%;
  background: #3063fd;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 3.66px;
    height: 3.66px;
    border-radius: 50%;
    background: #f2f5fe;
  }
`

const DataPoint = styled.div`
  position: absolute;
  border-radius: 50%;
  background: var(--t-colors-red-500);
  z-index: 7;
`

/**
 * OSINT-style sweeping radar visual shared by the Phishing Simulation Agent
 * banner and the Red Team risk widgets. Pass `testIdPrefix` to apply the PSIMA
 * QA data-testids; `showDataPoints` toggles the red exposure markers.
 */
const RiskExposureRadar = ({ showDataPoints = true, testIdPrefix }) => {
  const withTestId = (suffix) => (testIdPrefix ? `${testIdPrefix}_${suffix}` : undefined)

  return (
    <RadarContainer data-testid={withTestId('Radar_div')}>
      <RadarSweepProperty />
      <RadarCircle>
        <ConcentricCircle style={{ width: '96.365px', height: '96.365px', top: 0, left: 0 }} />
        <ConcentricCircle
          $dashed
          style={{ width: '79.134px', height: '79.134px', top: '8.62px', left: '8.62px' }}
        />
        <ConcentricCircle
          $dashed
          style={{ width: '61.265px', height: '61.265px', top: '17.55px', left: '17.55px' }}
        />

        <SweepLine data-testid={withTestId('SweepLine_div')} />
        <CenterDot />

        {showDataPoints &&
          RADAR_POINTS.map(({ key, top, left, size, opacity }) => (
            <DataPoint
              key={key}
              style={{ top, left, width: size, height: size, opacity }}
              data-testid={withTestId(`${key}_div`)}
            />
          ))}
      </RadarCircle>
    </RadarContainer>
  )
}

export default RiskExposureRadar
